<script setup lang="ts">

import { usePlayer } from '@/composables/game/player'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useConfig } from '@/composables/game/config'
import ChevronRightIcon from '@/components/icons/ChevronRightIcon.vue'
import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon.vue'
import type { Level } from '@/types/level'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import { twMerge } from 'tailwind-merge'
import LeadingBoardCard from '@/components/LeadingBoardCard.vue'
import type { LeaderboardPlayer } from '@/types/leaderboard-player'
import { useAuth } from '@/composables/auth'
import { useLeaderBoard } from '@/composables/game/leader-board'
import LevelProgressBar from '@/components/LevelProgressBar.vue'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import LevelInfoModal from '@/components/modals/LevelInfoModal.vue'
import { getLevelAssetUrl } from '@/helpers/game'
import { T } from '@tolgee/vue'
import webapp from '@/services/webapp'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'

const { player, experience, levelProgressPercentage } = usePlayer()
const { profile } = useAuth()
const { config } = useConfig()

const { getLeaderboard } = useLeaderBoard()

const loading = ref(false)

const levels = computed<Level[]>(() => config.value.game.levels)

const selectedLevel = ref<Level>(levels.value.find((level) => level.level === player.value.level) ?? levels.value[0])

const isCurrentLevel = computed(() => selectedLevel.value.level === player.value.level)
const nextLevelLimit = computed<number | null>(() => levels.value.find((level) => level.level === (player.value.level + 1))?.from ?? null)

const isPlayerOutOfBoard = ref(false)
const inScrollView = ref(false)
const showLevelInfoModal = ref(false)

const container = ref<HTMLElement | null>(null)

const playerForCard = computed<LeaderboardPlayer>(() => {
  return {
    firstName: profile.value.firstName,
    photoUrl: profile.value.photoUrl,
    profit: player.value.profit,
    own: true,
    isPremium: webapp.isPremium,
  }
})

const maxVisibleRank = ref<number>(100)
const listLimit = ref<number>(100)

const players = ref<LeaderboardPlayer[]>([])
const currentPlayerIndex = ref<number | null>(null)

const fullScreenStyles = computed(() => {
  return { bottom: `${webapp.safeAreaInset.bottom + 70}px` }
})

function onChangeLevel(shift: number) {
  selectedLevel.value = levels.value.find((level) => level.level === selectedLevel.value.level + shift) ?? selectedLevel.value
}

function isFirstLevel(): boolean {
  return selectedLevel.value.level === levels.value.reduce((l1, l2) => l1.level < l2.level ? l1 : l2).level
}

function isLastLevel(): boolean {
  return selectedLevel.value.level === levels.value.reduce((l1, l2) => l1.level > l2.level ? l1 : l2).level
}

function handleScroll() {
  if (container.value && currentPlayerIndex.value && isCurrentLevel.value) {
    const cardElement = container.value.querySelector(`.card:nth-child(${currentPlayerIndex.value})`) as HTMLElement
    if (cardElement) {
      const cardRect = cardElement.getBoundingClientRect()
      inScrollView.value = cardRect.bottom <= window.innerHeight - 90
    }
  }
}

watch(selectedLevel, async (selectedLevel) => {
  loading.value = true
  const {
    players: playerList,
    maxVisibleRank: max,
    thisPlayer,
    maxListLength,
  } = await getLeaderboard(selectedLevel.level)
  players.value = playerList
  maxVisibleRank.value = max
  listLimit.value = maxListLength
  currentPlayerIndex.value = thisPlayer.rank
  isPlayerOutOfBoard.value = !thisPlayer.isInList
  loading.value = false
}, { immediate: true })

watch([container, currentPlayerIndex], () => {
  nextTick(() => {
    handleScroll()
  })
}, { immediate: true })

onMounted(() => {
  document.getElementsByClassName('scroll-container')[0]?.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  document.getElementsByClassName('scroll-container')[0]?.removeEventListener('scroll', handleScroll)
})

</script>

<template>
  <div
    ref="container"
    :class="twMerge('pt-9 flex flex-col px-4 items-center relative w-full', !players.length && 'h-full flex-1')"
  >
    <InfoCircleIcon
      class="w-6 h-6 min-w-[24px] text-white/70 top-6 right-6 absolute cursor-pointer"
      @click="showLevelInfoModal = true"
    />
    <div class="flex items-center justify-center gap-x-4 mb-8">
      <div
        :class="twMerge('size-10 rounded-full  flex justify-center items-center z-10', isFirstLevel() && 'invisible')"
        @click="onChangeLevel(-1)"
      >
        <ChevronLeftIcon class="text-white/[87%]" />
      </div>
      <div class="h-[120px] w-[120px] relative">
        <img
          :src="getLevelAssetUrl(selectedLevel.level)"
          alt="Level image"
          class="h-[200px] w-[200px] min-w-[200px] object-contain absolute -translate-y-1/2 -translate-x-1/2 top-1/2 left-1/2"
        >
      </div>
      <div
        :class="twMerge('size-10 rounded-full flex justify-center items-center z-10', isLastLevel() && 'invisible')"
        @click="onChangeLevel(1)"
      >
        <ChevronRightIcon class="text-white/[87%] " />
      </div>
    </div>
    <div class="flex flex-col w-full items-center">
      <div class="flex flex-col text-center mb-6 items-center gap-y-1">
        <h1 class="text-light-white font-bold text-[32px] leading-[32px]">
          {{ selectedLevel.name }}
        </h1>
        <p class="text-light-white text-base font-medium tracking-tight opacity-70">
          <T
            key-name="levels.level"
            default-value="Level {index}"
            :params="{
              index: selectedLevel.level
            }"
          />
        </p>
      </div>

      <div
        v-if="isCurrentLevel"
        class="flex gap-x-3 w-full items-center mb-3 relative"
      >
        <LevelProgressBar
          :progress="levelProgressPercentage"
          progress-class="bg-xmas-gradient rounded-[100px]"
          progress-bar-class="h-8 p-[3.2px] rounded-[100px]"
          show-progress
        />
        <div
          class=" flex items-center w-max text-nowrap text-sm font-semibold -tracking-[0.28px] text-solid-white absolute right-3 progress-text-color-change"
        >
          <NumberFormatTooltip
            :number="experience"
            :formatting-type="'semi'"
          >
            <span>
              {{ formatNumberSemiTruncate(experience) }}
            </span>
          </NumberFormatTooltip>
          <span>/</span>
          <NumberFormatTooltip
            :number="nextLevelLimit"
            :formatting-type="'semi'"
          >
            <span>
              {{ formatNumberSemiTruncate(nextLevelLimit) }}
            </span>
          </NumberFormatTooltip>
        </div>
      </div>

      <p
        v-else
        class="font-semibold text-sm -tracking-[0.28px] text-solid-white mb-3 py-3"
      >
        <T
          key-name="levels.from"
          default-value="from"
        />
        <NumberFormatTooltip
          :number="selectedLevel.from"
          :formatting-type="'semi'"
        >
          <span>{{ ` ${formatNumberSemiTruncate(selectedLevel.from)}` }}</span>
        </NumberFormatTooltip>
      </p>

      <p
        v-if="isCurrentLevel"
        class="mb-8 text-xs font-medium text-white/60 text-center"
      >
        <T
          key-name="leaderboard.general.description"
          default-value="Invest in upgrades to gain experience. Leveling up will boost your profit per tap, energy and give you extra spins on the wheel."
        />
      </p>
    </div>
    <div
      v-if="!loading"
      :class="twMerge('w-full h-full flex flex-col', !players.length && 'flex-1')"
    >
      <div
        v-if="players.length"
        :class="twMerge('flex flex-col w-full gap-y-3 pb-4 items-center')"
      >
        <LeadingBoardCard
          v-for="(leadingBoardPlayer, index) in players.slice(0, maxVisibleRank)"
          :key="index"
          :player="leadingBoardPlayer"
          :index="(index+1).toString()"
          :is-own="index+1 === currentPlayerIndex"
          class="card"
        />
        <p
          v-if="(isCurrentLevel && isPlayerOutOfBoard) || (!isCurrentLevel && listLimit <= players.length)"
          class="mt-3 mb-6 text-center max-w-[314px] text-base font-medium text-white/60"
        >
          <T
            key-name="levels.many_more"
            default-value="And many more players who didn’t make it into the top {limit}... yet."
            :params="{limit: Math.min(players.length, listLimit)}"
          />
        </p>
        <LeadingBoardCard
          v-if="isPlayerOutOfBoard && isCurrentLevel"
          :player="playerForCard"
          :index="`${(maxVisibleRank).toString()}+`"
          card-class="invisible"
        />
      </div>
      <div
        v-else
        class="flex flex-1 h-full flex-col items-center justify-center gap-y-3 pb-8 w-full text-center"
      >
        <h2 class="text-[32px] leading-[35px] -tracking-[0.64px] font-bold text-light-white">
          <T
            key-name="levels.empty.title"
            default-value="Nothing to see here yet"
          />
        </h2>
        <p class="text-base leading-[17.6px] font-medium tracking-tight text-white">
          <T
            key-name="levels.empty.description"
            default-value="Once users reach this level you will see a list here"
          />
        </p>
      </div>
      <div
        v-if="isCurrentLevel && (isPlayerOutOfBoard || !inScrollView) "
        class="w-full fixed pr-8 z-20"
        :style="fullScreenStyles"
      >
        <LeadingBoardCard
          :player="playerForCard"
          :index="currentPlayerIndex === null ? `${(maxVisibleRank).toString()}+` : currentPlayerIndex?.toString() ?? ''"
          is-own
          card-class="border-green-primary"
        />
      </div>
    </div>
  </div>

  <LevelInfoModal
    v-if="showLevelInfoModal"
    :show="showLevelInfoModal"
    :current-level="player.level"
    @hide="showLevelInfoModal = false"
  />
</template>
<style lang="scss">
.progress-bar-leaderboard {
	background: linear-gradient(270deg, #E2FFA5 -18.66%, #C6F36B 97.63%);
}

.progress-text-color-change {
	@apply font-semibold;
	mix-blend-mode: difference;
}

</style>
